/**
 * MatchmakingCooldownManager
 *
 * Owns the wall-clock cooldown window during which a host suppresses
 * matchmakeability. Used for games whose own progression has a real-world
 * wait between attempts (e.g. Hitman's 30-minute contract clock).
 *
 * Single responsibility: track an active cooldown for a single host process.
 * No coordinator wiring lives here — `PavilionCoordinator` composes this
 * manager and translates trigger/invalidation events into method calls.
 *
 * Authority decision (v1): host-side enforcement only. The API persists the
 * campaign config and ignores the resulting `gameState: 'cooldown'` for now.
 * See docs/feature-plans/matchmaking-cooldown/EXECUTION_PLAN.md for trade-offs.
 */

import { CoordinatorEvent, CoordinatorEventImpl } from '../../_types/events';

export interface MatchmakingCooldownManagerDeps {
  /**
   * Wall-clock provider. Defaults to `Date.now`. Override for deterministic tests.
   */
  now?: () => number;
  /**
   * Timer scheduler. Defaults to global `setTimeout`. Override for deterministic tests.
   */
  setTimeoutFn?: typeof setTimeout;
  /**
   * Timer canceller. Defaults to global `clearTimeout`. Override for deterministic tests.
   */
  clearTimeoutFn?: typeof clearTimeout;
}

export interface CooldownStartInput {
  /**
   * Identifier for the launch instance the cooldown is bound to. Re-calling
   * `start` with the same `launchInstanceId` while the cooldown is active is
   * a no-op (idempotency). A different id replaces the existing cooldown.
   */
  launchInstanceId: string;
  /**
   * Cooldown duration in milliseconds. `<= 0` is a no-op (cooldown disabled).
   */
  periodMs: number;
}

export interface CooldownChangedEvent {
  active: boolean;
  cooldownEndsAt: number | null;
  /**
   * - `'started'`: a new cooldown began.
   * - `'expired'`: the active cooldown reached its end naturally.
   * - `'cleared'`: the active cooldown was cancelled by an invalidation event.
   */
  reason: 'started' | 'expired' | 'cleared';
}

interface ActiveCooldown {
  launchInstanceId: string;
  cooldownEndsAt: number;
  timer: ReturnType<typeof setTimeout> | null;
}

export class MatchmakingCooldownManager {
  private readonly now: () => number;
  private readonly setTimeoutFn: typeof setTimeout;
  private readonly clearTimeoutFn: typeof clearTimeout;

  private active: ActiveCooldown | null = null;

  public readonly events: { changed: CoordinatorEvent<CooldownChangedEvent> } = {
    changed: new CoordinatorEventImpl<CooldownChangedEvent>(),
  };

  constructor(deps: MatchmakingCooldownManagerDeps = {}) {
    this.now = deps.now ?? Date.now;
    this.setTimeoutFn = deps.setTimeoutFn ?? setTimeout;
    this.clearTimeoutFn = deps.clearTimeoutFn ?? clearTimeout;
  }

  /**
   * Start (or replace) the cooldown for a launch instance.
   *
   * Idempotent for the same `launchInstanceId`. A new id replaces the active
   * cooldown atomically: the previous timer is cancelled and a fresh `started`
   * event is emitted (no `cleared` event in between, since the conceptual
   * intent is "this is the new authoritative cooldown").
   */
  public start(input: CooldownStartInput): void {
    const { launchInstanceId, periodMs } = input;

    if (periodMs <= 0) {
      return;
    }

    if (this.active && this.active.launchInstanceId === launchInstanceId) {
      return;
    }

    if (this.active) {
      this.cancelTimer(this.active);
    }

    const cooldownEndsAt = this.now() + periodMs;

    const next: ActiveCooldown = {
      launchInstanceId,
      cooldownEndsAt,
      timer: null,
    };

    next.timer = this.setTimeoutFn(() => {
      if (this.active === next) {
        this.active = null;
        this.events.changed.emit({
          active: false,
          cooldownEndsAt: null,
          reason: 'expired',
        });
      }
    }, periodMs);

    this.active = next;

    this.events.changed.emit({
      active: true,
      cooldownEndsAt,
      reason: 'started',
    });
  }

  /**
   * Clear an active cooldown (campaign switch, macro re-run, prewarming, idle, etc).
   * No-op if no cooldown is active.
   *
   * @param reason — descriptive label for logs. Not used by the manager itself.
   */
  public clear(_reason: string): void {
    if (!this.active) {
      return;
    }

    this.cancelTimer(this.active);
    this.active = null;

    this.events.changed.emit({
      active: false,
      cooldownEndsAt: null,
      reason: 'cleared',
    });
  }

  /**
   * Wall-clock check. Authoritative — does not trust the timer alone, so a
   * delayed timer fire never grants matchmakeability that the wall clock has
   * not yet earned.
   */
  public isInCooldown(now?: number): boolean {
    if (!this.active) {
      return false;
    }
    const wallClock = now ?? this.now();
    return wallClock < this.active.cooldownEndsAt;
  }

  public cooldownEndsAt(): number | null {
    return this.active ? this.active.cooldownEndsAt : null;
  }

  public currentLaunchInstanceId(): string | null {
    return this.active ? this.active.launchInstanceId : null;
  }

  /**
   * Cancel any pending timer without emitting. For hot reload / test cleanup.
   */
  public dispose(): void {
    if (this.active) {
      this.cancelTimer(this.active);
      this.active = null;
    }
  }

  private cancelTimer(cooldown: ActiveCooldown): void {
    if (cooldown.timer !== null) {
      this.clearTimeoutFn(cooldown.timer);
      cooldown.timer = null;
    }
  }
}
